const RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10000, 20000, 30000];

export function getReconnectDelay(attempt: number): number {
  const index = Math.min(attempt, RECONNECT_DELAYS_MS.length - 1);
  return RECONNECT_DELAYS_MS[index];
}

export interface ReconnectingWebsocketHandlers {
  onOpen?: (ws: WebSocket) => void;
  onMessage?: (e: { data: string }) => void;
  onClose?: (e: { code: number; reason: any }) => void;
}

export interface ReconnectingWebsocketOptions {
  getUrl: () => Promise<string>;
  createWebSocket: (url: string) => WebSocket;
  handlers: ReconnectingWebsocketHandlers;
  setTimeoutFn?: typeof setTimeout;
  clearTimeoutFn?: typeof clearTimeout;
}

export class ReconnectingWebsocket {
  private ws: WebSocket | undefined;
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private closedIntentionally = false;
  private readonly setTimeoutFn: typeof setTimeout;
  private readonly clearTimeoutFn: typeof clearTimeout;

  constructor(private readonly options: ReconnectingWebsocketOptions) {
    const setTimeoutImpl = options.setTimeoutFn ?? setTimeout;
    const clearTimeoutImpl = options.clearTimeoutFn ?? clearTimeout;
    this.setTimeoutFn = ((fn: () => void, delay?: number) =>
      setTimeoutImpl(fn, delay)) as typeof setTimeout;
    this.clearTimeoutFn = (id: Parameters<typeof clearTimeout>[0]) =>
      clearTimeoutImpl(id);
  }

  get socket(): WebSocket | undefined {
    return this.ws;
  }

  async connect(): Promise<void> {
    this.closedIntentionally = false;
    this.clearReconnectTimer();

    const url = await this.options.getUrl();
    if (this.closedIntentionally) {
      return;
    }

    const ws = this.options.createWebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.attempt = 0;
      this.options.handlers.onOpen?.(ws);
    };
    ws.onmessage = (e) => {
      this.options.handlers.onMessage?.(e);
    };
    ws.onclose = (e) => {
      this.options.handlers.onClose?.(e);
      if (!this.closedIntentionally) {
        this.scheduleReconnect();
      }
    };
    ws.onerror = () => {
      // onclose fires after onerror for a failed connection; reconnection is
      // handled there so we don't schedule it twice.
    };
  }

  close(): void {
    this.closedIntentionally = true;
    this.clearReconnectTimer();
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
    }
    this.ws = undefined;
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      return;
    }
    const delay = getReconnectDelay(this.attempt);
    this.attempt += 1;
    this.reconnectTimer = this.setTimeoutFn(() => {
      this.reconnectTimer = undefined;
      if (!this.closedIntentionally) {
        this.connect();
      }
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      this.clearTimeoutFn(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
  }
}
